"use client";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Table, TableBody, TableCell, TableHeader, TableRow } from "../../../components/ui/table";
import { AngleDownIcon, AngleUpIcon } from "../../../icons";
import Pagination from "./Pagination";

const apiUrl = process.env.REACT_APP_API_URL;

interface Survey {
  diarysurvey_id: number;
  syscoregrp_id: number;
  cst: number;
  dateadded: string;
  user_id: number;
  venue: string;
  username: string;
  customername: string;
  memnumber: number;
  tablename: number;
  email: string;
  phone: number;
  staffname: string;
}

interface ApiResponse {
  count: number;
  data: Survey[];
  success: boolean;
}

interface Filters {
  dateBefore: string;
  dateAfter: string;
  manager: string;
  venue: string;
}

const formatDateTime = (dateString: string) => {
  // Remove the Z so it's not interpreted as UTC
  const localDateString = dateString.replace(/Z$/, '');
  // local time formatte
  const date = new Date(localDateString); 

  const datePart = date.toLocaleDateString('en-AU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });

  const timePart = date.toLocaleTimeString('en-AU', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  return `${datePart} ${timePart}`;
};

export default function CustomerSurveysTable({ filters }: { filters: Filters }) {
  const [surveys, setSurveys] = useState<Survey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 30;
  const navigate = useNavigate();

  const fetchSurveys = async (page: number = currentPage) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    
    try {
      const params = new URLSearchParams();
      
      // Add filters
      if (filters.dateBefore) params.append('dateBefore', filters.dateBefore);
      if (filters.dateAfter) params.append('dateAfter', filters.dateAfter);
      if (filters.manager) params.append('manager', filters.manager);
      if (filters.venue) params.append('venue', filters.venue);
      
      // Add pagination parameters
      params.append('page', page.toString());
      params.append('limit', itemsPerPage.toString());
      
      const response = await fetch(
        `${apiUrl}/customer_surveys?${params.toString()}`,
        {
          headers: {
            "Content-Type": "application/json",
            "token": token || "",
          }
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result: ApiResponse = await response.json();
      
      
      if (result.success) {
        setTotalCount(result.count);
        setSurveys(result.data);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error("Error fetching surveys:", err);
      setError(err instanceof Error ? err.message : "An unknown error occurred");
    } finally {
      setLoading(false);
    }
  };

  // Debounced fetch for filter changes
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchSurveys(1); // Reset to first page when filters change
    }, 500);
    
    return () => clearTimeout(timer);
  }, [filters]);

  // Handle page changes
  const handlePageChange = (page: number) => {
    fetchSurveys(page);
  };

  // Initial load
  useEffect(() => {
    fetchSurveys();
  }, []);

  // Render loading state
  if (loading && currentPage === 1) {
    return <div>Loading surveys...</div>;
  }

  // Render error state
  if (error) {
    return <div>Error: {error}</div>;
  }

  // Render no surveys found
  if (surveys.length === 0 && !loading) {
    return <div>No surveys found</div>;
  }

  const handleRowClick = (survey: Survey) => {
    // Navigate to the AddNewSurvey form with the survey data
    navigate("/add-new-survey", { state: {survey } });   
  };

  return (
    <>
      <Table>
        <TableHeader className="bg-[#d9ffa3]">
          <TableRow>
            <TableCell isHeader className="px-4 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Date Added
                  </span>
                </div>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
            <TableCell isHeader className="px-2 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <p className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                  Venue
                </p>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
            <TableCell isHeader className="px-2 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <p className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                  Manager
                </p>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
            <TableCell isHeader className="px-2 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <p className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                  Customer Name
                </p>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
            <TableCell isHeader className="px-2 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <p className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                  Member Number
                </p>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
            <TableCell isHeader className="px-2 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <p className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                  Table
                </p>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
            <TableCell isHeader className="px-2 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Email
                  </span>
                </div>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
            <TableCell isHeader className="px-2 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <p className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                  Contact Number
                </p>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
            <TableCell isHeader className="px-2 py-3 border border-gray-100 dark:border-white/[0.05]">
              <div className="flex items-center justify-between cursor-pointer">
                <div className="flex gap-3">
                  <span className="font-semibold text-gray-700 text-theme-xs dark:text-gray-400">
                    Staff Name
                  </span>
                </div>
                <button className="flex flex-col gap-0.5">
                  <AngleUpIcon className="text-gray-300 dark:text-gray-700" />
                  <AngleDownIcon className="text-gray-300 dark:text-gray-700" />
                </button>
              </div>
            </TableCell>
          </TableRow>
        </TableHeader>
        <TableBody>
          {surveys.map((survey) => (
            <TableRow key={survey.diarysurvey_id} className="cursor-pointer hover:bg-[#ffffa3]" onClick={() => handleRowClick(survey)}>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
              <span>{formatDateTime(survey.dateadded)}</span>
              </TableCell>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-gray-400 whitespace-normal">
                <span>{survey.venue}</span>
              </TableCell>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-white/90 whitespace-normal">
                <span>{survey.username}</span>
              </TableCell>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-white/90 whitespace-normal">
                <span>{survey.customername}</span>
              </TableCell>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-white/90 whitespace-normal">
                <span>{survey.memnumber}</span>
              </TableCell>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-white/90 whitespace-normal">
                <span>{survey.tablename}</span>
              </TableCell>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-white/90 whitespace-normal">
                <span>{survey.email}</span>
              </TableCell>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-white/90 whitespace-normal">
                <span>{survey.phone}</span>
              </TableCell>
              <TableCell className="px-4 py-4 font-normal text-gray-800 border border-gray-100 dark:border-white/[0.05] text-xs dark:text-white/90 whitespace-normal">
                <span>{survey.staffname}</span>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <div className="border border-t-0 rounded-b-xl border-gray-100 py-2 pl-[18px] pr-4 dark:border-white/[0.05]">
        <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between">
          <div className="pb-3 xl:pb-0">
            <p className="pb-3 text-sm font-semibold text-center text-gray-500 border-b border-gray-100 dark:border-gray-800 dark:text-gray-400 xl:border-b-0 xl:pb-0 xl:text-left">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {
                // Correct calculation for last page
                Math.min(currentPage * itemsPerPage, totalCount)
              } of {totalCount} entries
            </p>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={Math.ceil(totalCount / itemsPerPage)}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </>
  );
}